import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from "@angular/core";
import { Parser } from "json-logic-parser";
import { ToastrService } from "ngx-toastr";
import { ComponentFormulas } from "../../../models/component-formulas";
import { FormulaRow } from "../../../models/formula-row";
import { Store } from "../../../services/store.service";
import { CostView } from "../../../models/infix-formula/costView";
import { ComponentFormulaControllerService } from "../../../services/form-controller/component-formula-controller.service";
import { InfixFormula } from "../../../models/infix-formula/infix-formula";
import { CostService } from "../../../services/cost-controller/cost.service";
import {TranslatedBaseError} from "../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";
import {DeterminantControllerService} from "../../../services/registration-controller/determinant-controller.service";

@Component({
  selector: "component-formulas",
  templateUrl: "./component-formulas.component.html",
  styles: [],
  providers: [],
})
export class ComponentFormulasComponent extends TranslatedBaseError implements OnInit, OnChanges, OnDestroy {

  @Input() public serviceId: string;
  @Input() public componentKey: string;
  @Input() public componentFormulaId: string;
  @Input() public costView: CostView;
  @Input() public propertiesLoaded: boolean;
  @Input() public determinantMapById: { [key: string]: string };
  @Output() public formulaChanged = new EventEmitter();
  public componentFormula: ComponentFormulas = new ComponentFormulas();
  public newRow: FormulaRow = new FormulaRow();
  public selectedFormulaRow: FormulaRow = new FormulaRow();
  public showDeterminantBuilder: boolean = false;
  public selectedIndex: number;
  public listToggle: boolean = false;
  public formulasLoaded: boolean = false;
  public formulasPrettified: boolean = false;
  public infixToJsonLogicParser: Parser;

  public showDrawerHeader: boolean = true;

  public subscriptionFormula: any;

  constructor(private costService: CostService,
              private determinantService: DeterminantControllerService,
              private componentFormulaService: ComponentFormulaControllerService,
              public store: Store,
              toastr: ToastrService, public translateService: TranslateService) {
    super(toastr, translateService);
    this.infixToJsonLogicParser = new Parser();
  }

  public ngOnInit(): void {
    this.initCostView();
    this.subscriptionFormula = this.costService.changeFormula.subscribe((data) => {
      this.costView = data.costView;
      this.setupInfixFormula();
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.formulasLoaded && !this.formulasPrettified
        && !changes.propertiesLoaded?.previousValue && changes.propertiesLoaded?.currentValue) {
      this.prettyPrint();
    }
  }

  public dropped(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.saveComponentFormula();
    }
  }

  public fixCostItemIndexes() {
    this.componentFormula.formulaRows.forEach((it, index, itemsArray) => {
      itemsArray[index].sortOrder = index;
    });
  }

  public ngOnDestroy() {
    this.subscriptionFormula.unsubscribe();
  }

  public isFormulaSpecified() {
    if (!this.costView.formula) { return false; }
    return this.costView.formula !== `(${this.costView.infixSeparator})`
        && this.costView.formula !== this.costView.emptyFormula;
  }

  public initCostView() {
    if (!this.costView) { return; }
    this.costView.value = true;
    this.loadComponentFormula();
  }

  public setupInfixFormula() {
    let formulaObj: any;
    let formulaBroken = false;
    let jsonLogicFormula = [{}];
    try {
      if (this.isFormulaSpecified()) {
        jsonLogicFormula = this.infixToJsonLogicParser.parse(this.costView.formula.replace(new RegExp(this.costView.infixSeparator, "g"), " "));
        formulaObj = new InfixFormula(this.costView.formula, this.costView, this.translateService);
      } else {
        formulaObj = false;
      }

    } catch (err) {
      jsonLogicFormula = [{}];
      formulaBroken = true;
      formulaObj = false;
    }
    if (this.costView.formulaIndex === "new") {
      this.newRow.infix = this.costView.formula;
      this.newRow.jsonLogic = jsonLogicFormula[0];
      this.newRow.prettyPrintInfixFormula = formulaBroken ? "NaN" : (formulaObj ? formulaObj.prettyPrint() : this.costView.emptyFormula);
      this.newRow.meta = this.costView.formulaMeta;
    } else {
      this.componentFormula.formulaRows[this.costView.formulaIndex].infix = this.costView.formula;
      this.componentFormula.formulaRows[this.costView.formulaIndex].jsonLogic = jsonLogicFormula[0];
      this.componentFormula.formulaRows[this.costView.formulaIndex].prettyPrintInfixFormula = formulaBroken ? "NaN" : (formulaObj ? formulaObj.prettyPrint() : this.costView.emptyFormula);
      this.componentFormula.formulaRows[this.costView.formulaIndex].meta = this.costView.formulaMeta;
      this.saveComponentFormula();
    }
  }

  public addNewRow(): void {
    if (!this.componentFormula) {
      this.componentFormula = new ComponentFormulas();
    }
    this.newRow = new FormulaRow();
    this.componentFormula.formulaRows.push(this.newRow);
    /*
    if (this.newRow.infix) {
      const copy: FormulaRow = JSON.parse(JSON.stringify(this.newRow));
      copy.prettyPrintFinished = true;
      this.componentFormula.formulaRows.push(copy);
      this.newRow = new FormulaRow();
      this.saveComponentFormula();
    }
      */
  }

  public createRowAndOpenBuilder(): void {
    const existingEmpty = this.componentFormula?.formulaRows.find(r => ComponentFormulasComponent.isWithoutCondition(r));
    if (existingEmpty) {
      this.openDeterminantModal(existingEmpty);
    } else {
      this.addNewRow();
      this.openDeterminantModal(this.componentFormula.formulaRows[this.componentFormula.formulaRows.length - 1]);
    }
  }

  public createRowAndOpenFormula(): void {
    const existingEmpty = this.componentFormula?.formulaRows.find(r => !r.infix);
    if (existingEmpty) {
      const index = this.componentFormula.formulaRows.indexOf(existingEmpty);
      this.openInfixFormula(index);
    } else {
      this.addNewRow();
      this.openInfixFormula(this.componentFormula.formulaRows.length - 1);
    }
  }

  private static isWithoutCondition(v: FormulaRow) {
    return !v.jsonDeterminants;
  }

  public isEmptyConditionInTablePresented(): boolean {
    const itemWithoutCondition = this.componentFormula.formulaRows.find(ComponentFormulasComponent.isWithoutCondition);
    return !!itemWithoutCondition;
  }

  public isAddButtonDisabled(): boolean {
    const itemWithoutCondition = this.componentFormula.formulaRows.find(ComponentFormulasComponent.isWithoutCondition);
    if ((!itemWithoutCondition || this.newRow.jsonDeterminants) && this.newRow.infix) {
      return this.newRow.prettyPrintInfixFormula === "NaN";
    }
    return true;
  }

  public showModalHeader(e) {
    console.log(e);
    this.showDrawerHeader = e;
  }

  public openDeterminantModal(formulaRow: FormulaRow) {
    if (formulaRow) {
      this.selectedFormulaRow = formulaRow;
    } else {
      this.createNewFormulaRow();
    }
    this.showDeterminantBuilder = true;
    this.showAsFullDrawer();
  }

  public createNewFormulaRow() {
    this.selectedFormulaRow = new FormulaRow();
    // this.componentFormula.formulaRows.push(this.selectedFormulaRow);
  }

  public closeDeterminantLayer() {
    this.showDeterminantBuilder = false;
    this.backAsFramedDrawer();
    // this.editCancel.emit({cancel: true});
  }

  public showAsFullDrawer() {
    const windowParent = window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-content > div > div");
    if (windowParent) {
      windowParent.querySelector("iframe")?.classList.add("full-drawer");
      windowParent.querySelector(".page-iframe")?.classList.add("full-drawer-content");
      windowParent.querySelector(":scope > .row:nth-child(1)")?.classList.add("d-none");
      // windowParent.querySelector(":scope > .row:nth-child(2)")?.classList.add("d-none");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector("#myBuilder").classList.add("m-0");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector(".tab-pane.active").classList.add("p-0", "m-0");
      // windowParent.querySelector(":scope > .row:last-child")?.classList.add("d-none");
      window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-close")?.classList.add("d-none");
      windowParent.querySelector("#element1")?.querySelector(":scope > .formio-component-tabs")?.querySelector(":scope > .nav-tabs")?.classList.add("d-none");
    }
  }

  public backAsFramedDrawer() {
    const windowParent = window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-content > div > div");
    if (windowParent) {
      windowParent.querySelector("iframe")?.classList.remove("full-drawer");
      windowParent.querySelector(".page-iframe")?.classList.remove("full-drawer-content");
      windowParent.querySelector(":scope > .row:nth-child(1)")?.classList.remove("d-none");
      // windowParent.querySelector(":scope > .row:nth-child(2)")?.classList.remove("d-none");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector("#myBuilder").classList.remove("m-0");
      windowParent.querySelector(":scope > .row:nth-child(2)")?.querySelector(".tab-pane.active")?.classList.remove("p-0", "m-0");
      // windowParent.querySelector(":scope > .row:last-child")?.classList.remove("d-none");
      window.parent.document.querySelector(".component-settings")?.querySelector(".formio-dialog-close")?.classList.remove("d-none");
      windowParent.querySelector("#element1")?.querySelector(":scope > .formio-component-tabs")?.querySelector(":scope > .nav-tabs")?.classList.remove("d-none");
    }
  }

  public closeEdit() {
    this.store.state.common.determinantSection.topLayer = "list";
    this.store.state.common.determinantSection.determinant = {};
  }

  public setListView(b: boolean) {
    this.listToggle = b;
  }

  public isDeterminantEmpty(jsonDeterminant: string): boolean {
    if (!jsonDeterminant) {
      return true;
    }
    try {
      const parsed = JSON.parse(jsonDeterminant);
      return !(Array.isArray(parsed) && parsed[0]?.items?.length > 0);
    } catch {
      return true;
    }
  }

  public addNewInfixFormula(): void {
    window.scrollTo(0, 0);
    if (!this.newRow.infix) {
      this.newRow.infix = "";
    }
    if (!this.newRow.meta) {
      this.newRow.meta = { name: "Drag and drop your formula below", isNew: true };
    }
    this.costView.isInfix = true;
    this.costView.formula = this.newRow.infix;
    this.costView.formulaMeta = this.newRow.meta;
    this.costView.formulaType = "formula";
    this.costView.formulaIndex = "new";
    this.costView.value = false;
    this.showAsFullDrawer();
  }

  public openInfixFormula(index: any): void {
    window.scrollTo(0, 0);
    const fci = this.componentFormula.formulaRows[index];
    fci.prettyPrintFinished = true; // either it was pretty already or it will come prettified courtesy of formula builder
    this.costView.isInfix = true;
    this.costView.formula = fci.infix;
    this.costView.formulaMeta = fci.meta;
    this.costView.formulaType = "formula";
    this.costView.formulaIndex = index;
    this.costView.value = false;
    this.showAsFullDrawer();
  }

  private loadComponentFormula() {
    this.formulasLoaded = false;
    this.formulasPrettified = false;
    const cb = (data: ComponentFormulas) => {
      this.componentFormula = data || new ComponentFormulas();
      this.componentFormulaId = this.componentFormula.id;
      this.formulasLoaded = true;
      if (this.propertiesLoaded) { this.prettyPrint(); }
    };
    if (this.componentFormulaId) {
      this.componentFormulaService.getById(this.componentFormulaId).subscribe(cb, (error) => this.showErrorMessage(error));
    } else {
      this.componentFormulaService.getComponentFormula(this.serviceId, this.componentKey).subscribe(cb, (error) => this.showErrorMessage(error));
    }
  }

  private prettyPrint() {
    this.formulasPrettified = false;
    const infixFormula = new InfixFormula(this.costView.emptyFormula, this.costView, this.translateService);
    this.componentFormula.formulaRows.forEach((row) => {
      infixFormula.value = row.infix;
      infixFormula.initProperties();
      row.prettyPrintInfixFormula = infixFormula.prettyPrint();
      row.prettyPrintFinished = true;
    });
    this.formulasPrettified = true;
  }

  public updateDeterminant(event: any) {
    console.log(event.jsonString);
    this.selectedFormulaRow.jsonDeterminants = event.jsonString;
    /*
    if (this.selectedFormulaRow) {
      this.selectedFormulaRow.jsonDeterminants = event.jsonString;
      if (this.selectedFormulaRow.id) {
        this.saveComponentFormula();
      } else {
        this.newRow.jsonDeterminants = event.jsonString;
      }
    }
    */
  }

  private static sendSaveProcessingEvent(state = true) {
    window.top.postMessage([{channel: "FormBuilder", message: JSON.stringify({event: "saveProcessing", state})}], "*");
  }

  public saveComponentFormula() {
    ComponentFormulasComponent.sendSaveProcessingEvent();
    this.fixCostItemIndexes();
    const cb = (cf: ComponentFormulas) => {
      this.responseSuccessful("Component formula updated");
      Object.assign(this.componentFormula, cf);
      this.componentFormula.formulaRows.forEach((row) => row.prettyPrintFinished = true);
      this.componentFormulaId = cf.id;
      this.formulaChanged.emit(this.componentFormula);
      this.newRow = new FormulaRow();
      ComponentFormulasComponent.sendSaveProcessingEvent(false);
    };
    const errorCb = (error) => {
      this.showErrorMessage(error);
      ComponentFormulasComponent.sendSaveProcessingEvent(false);
    };

    if (this.componentFormulaId) {
      this.componentFormulaService.save(this.componentFormulaId, this.componentFormula).subscribe(cb, errorCb);
    } else {
      this.componentFormulaService.saveComponentFormula(this.serviceId, this.componentKey, this.componentFormula)
        .subscribe(cb, errorCb);
    }
    this.closeDeterminantLayer();
  }

  public deleteRow(index: number): void {
    if (index > -1) {
      this.componentFormula.formulaRows.splice(index, 1);
      this.saveComponentFormula();
    }
  }

}
